import { DBDef_Action, DBDef_OrderGroup } from '@app/pathway-shared';
import { _keys, asArray, dbObjectToId, deepClone, filterDuplicates, filterFlatInstances, filterInstances, ImplementationMissingException, MUSTNeverHappenException, sortArray } from '@nu-art/ts-common';
import { ModuleFE_Expression, ModuleFE_Variable } from '@app/hcs-frontend';
import { resolveResolutionFunctionVariables } from '../Component_ResolutionFunctionEditor/utils.js';
import { PathwayVariableContext_ResolutionFunctions, PathwayVariableContext_StatePreconditions, PathwayVariableContext_TransitionConsults, PathwayVariableContext_TransitionNurse, PathwayVariableContext_TransitionTests, PathwayVariableContext_TransitionTreatments } from './consts.js';
import { AccessLevel, ModuleFE_PermissionsAssert } from '@nu-art/permissions-frontend/modules/ModuleFE_PermissionsAssert';
import { ModuleFE_Utils } from '@nu-art/thunderstorm-frontend/index';
import { ModuleFE_Action, ModuleFE_OrderGroup, ModuleFE_Pathway, ModuleFE_PathwayState, ModuleFE_PathwayTransition } from '../../_entity.js';
import { OrgAccessLevel } from '@app/hcs-shared';
/**
 * Group transition by their target id and sort them by creation date
 * @param transitions Array of DB_Transitions
 * @returns transitions mapper grouped by targetId and sorted by created
 */
export const groupAndSortTransitions = (transitions) => {
    const targetIdMapper = {};
    transitions.forEach(transition => {
        if (!targetIdMapper[transition.targetId])
            targetIdMapper[transition.targetId] = [];
        targetIdMapper[transition.targetId].push(transition);
    });
    _keys(targetIdMapper).forEach(transitionGroup => sortArray(targetIdMapper[transitionGroup], (item) => item.__created));
    return targetIdMapper;
};
/**
 * Resolve variable id from an action from type order
 * @param id the id of the action item
 */
const resolveOrderActionVarId = (id) => {
    const action = ModuleFE_Action.cache.unique(id);
    if (!action)
        throw new MUSTNeverHappenException(`Could not find order for id ${id}`);
    if (!ModuleFE_Action.is.order(action))
        return;
    return action.variableId;
};
/**
 * Get all precondition variables ids
 * @returns a mapper mapping variable ids to their context
 */
export const getStatePreconditionsVariables = (states) => {
    const preconditionPointers = filterFlatInstances(states.flatMap(state => state.preconditions.map(precon => precon.itemPointer)));
    const orderIds = filterDuplicates(preconditionPointers.map(pointer => {
        switch (pointer.dbKey) {
            case DBDef_Action.dbKey:
                return pointer.id;
            case DBDef_OrderGroup.dbKey: {
                const group = ModuleFE_OrderGroup.cache.unique(pointer.id);
                if (!group)
                    throw new MUSTNeverHappenException(`Could not find order group for id ${pointer.id}`);
                return group.__orderIds;
            }
            default:
                throw new ImplementationMissingException(`Missing for db key ${pointer.dbKey}`);
        }
    }).flat());
    const variableIds = filterInstances(orderIds.map(orderId => resolveOrderActionVarId(orderId)));
    const variables = ModuleFE_Variable.cache.filter(variable => variableIds.includes(variable._id));
    return { [PathwayVariableContext_StatePreconditions]: sortArray(variables, variable => variable.name.toLowerCase()) };
};
/**
 * Generic function to resolve all variables from the relevant transition fields
 * @param transitions all of the transitions
 * @param key the key in the transition db entity
 */
const resolveTransitionActionVars = (transitions, key) => {
    const varIds = transitions.reduce((variableIds, transition) => {
        if (Array.isArray(transition[key])) {
            const orderIds = transition[key].map(pointer => {
                switch (pointer.dbKey) {
                    case DBDef_Action.dbKey:
                        return pointer.id;
                    case DBDef_OrderGroup.dbKey: {
                        const group = ModuleFE_OrderGroup.cache.unique(pointer.id);
                        if (!group)
                            throw new MUSTNeverHappenException(`Could not find order group for id ${pointer.id}`);
                        return group.__orderIds;
                    }
                    default:
                        throw new ImplementationMissingException(`Missing for db key ${pointer.dbKey}`);
                }
            }).flat();
            variableIds = [...variableIds, ...filterInstances((orderIds).map(id => resolveOrderActionVarId(id)))];
        }
        return variableIds;
    }, []);
    const variables = ModuleFE_Variable.cache.filter(variable => varIds.includes(variable._id));
    return sortArray(variables, (item) => item._nameInLowercase);
};
/**
 * Get all transitions variables
 * @returns a mapper mapping variable ids to their context
 */
export const getTransitionVariables = (pathwayTransitions) => {
    return {
        [PathwayVariableContext_TransitionTests]: resolveTransitionActionVars(pathwayTransitions, 'testsIds'),
        [PathwayVariableContext_TransitionTreatments]: resolveTransitionActionVars(pathwayTransitions, 'treatmentsIds'),
        [PathwayVariableContext_TransitionConsults]: resolveTransitionActionVars(pathwayTransitions, 'consultIds'),
        [PathwayVariableContext_TransitionNurse]: resolveTransitionActionVars(pathwayTransitions, 'nurseIds')
    };
};
/**
 * Get all resolution functions variables
 * @returns a mapper mapping variable ids to their context
 */
export const getAllResolutionFunctionsVariables = (resolutionFunctionsIds) => {
    const variableIds = resolveResolutionFunctionVariables(filterInstances(resolutionFunctionsIds.map(id => ModuleFE_Expression.cache.unique(id))));
    const variables = ModuleFE_Variable.cache.filter(variable => variableIds.includes(variable._id));
    return {
        [PathwayVariableContext_ResolutionFunctions]: sortArray(variables, (item) => item._nameInLowercase)
    };
};
/**
 * Update resolution function post state/transition changes
 * @param transitionId The current transition id to update
 * @param statesIds The source and target state ids for this target
 */
export const updateResolutionFunctionTitle = async (transitionId, statesIds) => {
    // resolve and assert relevant transition
    const transition = ModuleFE_PathwayTransition.cache.unique(transitionId);
    if (!transition)
        throw new MUSTNeverHappenException(`pathway transition with id ${transitionId} not found`);
    // resolve and assert pathway states
    const states = statesIds.map(id => {
        const pathwayState = ModuleFE_PathwayState.cache.unique(id);
        if (!pathwayState)
            throw new MUSTNeverHappenException('pathway state with id ${id} not found');
        return pathwayState;
    });
    // resolve and assert resolution function
    const resolutionFunction = deepClone(ModuleFE_Expression.cache.unique(transition.resolutionFunctionId));
    if (!resolutionFunction)
        throw new MUSTNeverHappenException(`could not find resolution function with id ${transition.resolutionFunctionId}`);
    // update label according the spec
    const labelToUpdate = `${states[0].name} => ${transition.label ?? states[1].name}`;
    // make sure not to upsert stuff with no changes
    if (labelToUpdate === resolutionFunction.label.trim())
        return;
    // set the expression label
    resolutionFunction.label = labelToUpdate;
    // upsert the updated label
    await ModuleFE_Expression.v1.upsert(resolutionFunction).executeSync();
};
export const basicSyncEventRefresh = (entityId, params, updateCallback, deleteCallback) => {
    switch (params[0]) {
        case 'delete':
        case 'delete-multi': {
            const deletedEntities = asArray(params[1]).map(dbObjectToId);
            if (deletedEntities.includes(entityId))
                return deleteCallback();
            break;
        }
        case 'update':
        case 'upsert-all':
        case 'create': {
            const updatedEntities = asArray(params[1]).map(dbObjectToId);
            if (updatedEntities.includes(entityId))
                return updateCallback();
            break;
        }
    }
};
export const checkIfPathwayElementChanged = (previousSet, params, isNewCallBack, updateCallback, deleteCallback) => {
    switch (params[0]) {
        case 'delete':
        case 'delete-multi': {
            const deletedEntities = asArray(params[1]);
            if (deletedEntities.some(deletedEntity => previousSet.some(entity => entity._id === deletedEntity._id)))
                return deleteCallback();
            break;
        }
        case 'update':
        case 'upsert-all':
        case 'create': {
            const updatedEntities = asArray(params[1]);
            if (updatedEntities.some(updatedEntity => previousSet.some(entity => entity._id === updatedEntity._id) || isNewCallBack(updatedEntity)))
                return updateCallback();
            break;
        }
    }
};
/**
 * Permission assertion function for the pathway editor
 * @param dbPathway The db pathway to assert permissions to
 */
export const canEditPathway = (dbPathway) => {
    const orgAccessLevel = ModuleFE_Utils.getProcessor('assert-org-permission')(dbPathway);
    const collectionAccessLevel = ModuleFE_PermissionsAssert.getAccessLevel(ModuleFE_Pathway.permissions.collection.edit);
    return orgAccessLevel === OrgAccessLevel.write && collectionAccessLevel === AccessLevel.HasAccess;
};
export const updateTransitionOrderExpression = async (editable, property, pointerId, expressionId) => {
    const currentPointers = editable.item[property] ?? [];
    const updatedPointers = currentPointers.map(p => {
        if (p.id !== pointerId)
            return p;
        const { expressionId: _, ...rest } = p;
        return expressionId ? { ...rest, expressionId } : rest;
    });
    await editable.updateObj({ [property]: updatedPointers });
};
//# sourceMappingURL=utils.js.map